import { useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';

import MainCard from 'components/MainCard';
import useApi from 'hooks/useApi';
import { diApi } from '../api';
import type { InboundDocumentsStatus } from '../types';
import { errorText, formatCount, relativeTime } from './format';

interface Props {
  clientUid: string;
  refreshKey?: number;
  onOpen: () => void;
}

/** Overview source card for files the client sends over SFTP (read by the prod Lambda). */
export default function SftpDocumentsCard({ clientUid, refreshKey = 0, onOpen }: Props) {
  const { showToast } = useApi();
  const toastRef = useRef(showToast);
  toastRef.current = showToast;
  const [status, setStatus] = useState<InboundDocumentsStatus | null>(null);
  const [failed, setFailed] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    diApi
      .getDocumentsStatus(clientUid)
      .then((res) => {
        if (cancelled) return;
        if (res.success === false) setFailed(true);
        else setStatus(res.data);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [clientUid, refreshKey]);

  const checkNow = async () => {
    setChecking(true);
    try {
      const res = await diApi.requestDocumentsScan(clientUid);
      if (res.success === false) toastRef.current(res.message || 'Could not start the check', 'error');
      else toastRef.current('Checking for new files. New loads appear on the Documents tab within a few minutes.', 'success');
    } catch (err) {
      toastRef.current(errorText(err, 'Could not start the check'), 'error');
    } finally {
      setChecking(false);
    }
  };

  const loads = status?.loads ?? {};
  const files = status?.files ?? {};
  const totalLoads = Object.values(loads).reduce((a, b) => a + (b ?? 0), 0);
  const filesRead = (files.DONE ?? 0) + (files.DUPLICATE ?? 0);
  const filesWaiting = (files.PENDING ?? 0) + (files.PROCESSING ?? 0);

  const tiles: Array<{ label: string; value: number | undefined; color?: string }> = [
    { label: 'Loads', value: totalLoads },
    { label: 'Complete', value: loads.COMPLETE, color: 'success.main' },
    { label: 'Incomplete', value: loads.INCOMPLETE, color: 'warning.dark' },
    { label: 'Needs review', value: loads.NEEDS_REVIEW, color: 'error.main' }
  ];

  return (
    <MainCard content={false} sx={{ p: 2.5, height: '100%' }}>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box>
            <Typography variant="h5">SFTP documents</Typography>
            <Typography variant="body2" color="text.secondary">
              BOLs, verification forms, licences and photos, read into one record per load
            </Typography>
          </Box>
          {status &&
            (status.enabled ? (
              <Chip size="small" color="success" variant="light" label="Receiving" sx={{ fontWeight: 600 }} />
            ) : (
              <Chip size="small" variant="outlined" label="No files yet" />
            ))}
        </Stack>

        {!status && !failed && <CircularProgress size={20} aria-label="Loading SFTP documents" />}
        {failed && (
          <Typography variant="body2" color="text.secondary">
            Could not load the document counts.
          </Typography>
        )}

        {status?.enabled && (
          <>
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              {tiles.map((t) => (
                <Box key={t.label}>
                  <Typography variant="caption" color="text.secondary">
                    {t.label}
                  </Typography>
                  <Typography variant="h4" sx={{ color: t.value ? t.color : undefined }}>
                    {formatCount(t.value ?? 0)}
                  </Typography>
                </Box>
              ))}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {formatCount(filesRead)} files read
              {filesWaiting ? ` · ${formatCount(filesWaiting)} waiting` : ''}
              {files.FAILED ? ` · ${formatCount(files.FAILED)} failed` : ''}
              {status.lastFileAt ? ` · last file ${relativeTime(status.lastFileAt)}` : ''}
            </Typography>
          </>
        )}

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button variant="outlined" color="secondary" onClick={onOpen}>
            Open documents
          </Button>
          {status?.canRequest && (
            <Button variant="text" color="secondary" onClick={checkNow} disabled={checking}>
              Check for new files
            </Button>
          )}
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ borderTop: 1, borderColor: 'divider', pt: 1.25 }}>
          Aljex Morning Sync (daily CSV backfill) is not set up yet.
        </Typography>
      </Stack>
    </MainCard>
  );
}
